import { defaultPlayersAbove, playersAbove } from "../document/band.ts";
import { applyCommand, applyPropRun, updatePropRunEnd, type Command, type FillCellValue, type UpdatePropCommand } from "../document/commands.ts";
import type { LayoutCommand } from "../document/layout-commands.ts";
import { plainInstanceFootprint } from "../document/footprint-cells.ts";
import { EMPTY_TILE, packedToTileRef } from "../document/packed-tile.ts";
import { sameJson } from "../document/same-json.ts";
import type { EntityInstance, JsonValue, LayerDocument, MapDocument, PropBehaviour, PropInstance, TileRef, TilesetRef, WorldDocument } from "../document/types.ts";
import { OP_FRAME_OVERHEAD, fillPatchesOf, patchesOf } from "./cell-patches.ts";
import { OP_CELLS_MAX, OP_FRAME_MAX, OP_INTENTS_MAX, type IgnoreChanges, type Intent, type KeyChanges, type LiveOp, type PatchGrid, type PatchValue } from "./op-types.ts";
import { cellsOf, encodeOp, parseOp } from "./ops.ts";
import { layoutIntents } from "./layout-ops.ts";
import { doorCommandIntents, doorListIntents } from "./door-ops.ts";
import { bandAfterPlacing, withoutContent, withoutLayerContent } from "./resolve-structure.ts";
import { tilesetIntents } from "./tileset-ops.ts";
import { layerOf, mapOf } from "./resolve-types.ts";

/**
 * `commandToOps` (ADR-086 decisions 19 and 20): a command the editor ran, as the operations a tab sends for
 * it. Every index and before-value a command carries is turned into what an intent carries instead: a
 * neighbour's id, a first layer above the band, the keys that changed. A compound travels as its parts in
 * order; something with content (a layer, a map) as its empty add followed by its content. The intents are
 * packed into as many operations of one edit group as the limits need.
 */

/**
 * Where a tab's operation ids stand: its tag and the counter the next operation takes. The operations of one
 * command take consecutive counters from it, and their edit group's id is the first one's.
 */
export interface OpIds {
  readonly tag: string;
  readonly next: number;
}

/**
 * A tile reference as an operation carries it: a flag only when set, and none on a frame.
 *
 * @param tile The reference.
 * @param flips Whether it may carry its flips, as a tile cell does.
 * @returns The reference with exactly the fields the codec reads.
 */
function tileWire(tile: TileRef, flips: boolean): TileRef {
  return { tileset: tile.tileset, tile: tile.tile, ...(flips && tile.flipX === true ? { flipX: true } : {}), ...(flips && tile.flipY === true ? { flipY: true } : {}) };
}

/**
 * Tile references keyed by name as an operation carries them.
 *
 * @param tiles The references.
 * @returns The same, each with exactly its fields.
 */
function tilesWire(tiles: { readonly [key: string]: TileRef }): { readonly [key: string]: TileRef } {
  return Object.fromEntries(Object.entries(tiles).map(([key, tile]) => [key, tileWire(tile, false)]));
}

/**
 * A behaviour as an operation carries it.
 *
 * @param behaviour The behaviour.
 * @returns The behaviour with exactly the fields the codec reads.
 */
function behaviourWire(behaviour: PropBehaviour): PropBehaviour {
  return {
    type: behaviour.type,
    fields: behaviour.fields,
    ...(behaviour.frames === undefined ? {} : { frames: tilesWire(behaviour.frames) }),
    ...(behaviour.animations === undefined ? {} : { animations: tilesWire(behaviour.animations) }),
    ...(behaviour.activation === undefined ? {} : { activation: behaviour.activation.map((cell) => [cell[0], cell[1]] as const) }),
  };
}

/**
 * A prop as an operation carries it.
 *
 * @param prop The prop.
 * @returns The prop with exactly the fields the codec reads.
 */
function propWire(prop: PropInstance): PropInstance {
  return { id: prop.id, frame: tileWire(prop.frame, false), x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY, sortPixels: prop.sortPixels, ...(prop.behaviour === undefined ? {} : { behaviour: behaviourWire(prop.behaviour) }), ...(prop.keepPlacement === true ? { keepPlacement: true as const } : {}), ...(prop.footprint === undefined ? {} : { footprint: plainInstanceFootprint(prop.footprint) }) };
}

/**
 * An entity as an operation carries it.
 *
 * @param entity The entity.
 * @returns The entity with exactly the fields the codec reads.
 */
function entityWire(entity: EntityInstance): EntityInstance {
  return { id: entity.id, type: entity.type, x: entity.x, y: entity.y, fields: entity.fields };
}

/**
 * The keys that differ between two records.
 *
 * @param before The record as it was.
 * @param after The record as it is.
 * @returns The keys to set and to remove, each part present only when it holds a key.
 */
function keyChanges(before: { readonly [key: string]: JsonValue }, after: { readonly [key: string]: JsonValue }): KeyChanges {
  const set = Object.fromEntries(Object.entries(after).filter(([key, value]) => !Object.hasOwn(before, key) || !sameJson(before[key], value)));
  const remove = Object.keys(before).filter((key) => !Object.hasOwn(after, key));
  return { ...(Object.keys(set).length === 0 ? {} : { set }), ...(remove.length === 0 ? {} : { remove }) };
}

/**
 * The grid a layer holds.
 *
 * @param layer The layer.
 * @returns The grid's kind and its cells, or undefined for a layer of props or entities.
 */
function gridOf(layer: LayerDocument): { readonly grid: PatchGrid; readonly cells: Readonly<Uint8Array> | Readonly<Uint32Array> } | undefined {
  switch (layer.kind) {
    case "terrain":
      return { grid: "corners", cells: layer.corners };
    case "collision":
      return { grid: "collision", cells: layer.cells };
    case "zone":
      return { grid: "zone", cells: layer.cells };
    case "tile":
      return { grid: "tiles", cells: layer.tiles };
    case "door":
      return { grid: "door", cells: layer.cells };
    default:
      return undefined;
  }
}

/**
 * Values numbered in the order they are first met, so two cells share a number exactly when they take the
 * same value.
 */
interface ValueCodes {
  readonly codes: Map<string, number>;
  readonly values: PatchValue[];
}

/**
 * The number of a value a command carries, numbering it when it is new.
 *
 * @param table The numbering.
 * @param value The value: a byte, a zone type's id, a door's id, a tile reference or null.
 * @returns The number.
 */
function codeFor(table: ValueCodes, value: FillCellValue): number {
  const wire: PatchValue = value !== null && typeof value === "object" ? tileWire(value, true) : value;
  const key = JSON.stringify(wire);
  const known = table.codes.get(key);
  if (known !== undefined) {
    return known;
  }
  table.codes.set(key, table.values.length);
  table.values.push(wire);
  return table.values.length - 1;
}

/**
 * The patches of a stroke: each cell's index and the value it takes, in any order.
 *
 * @param command The stroke.
 * @param grid The grid it writes.
 * @returns The patches; of two changes of one cell the later stands.
 */
function strokePatches(command: Extract<Command, { readonly kind: "paintCorners" | "paintCollision" | "paintZone" | "paintDoor" | "setTiles" }>, grid: PatchGrid): Intent[] {
  const latest = new Map<number, FillCellValue>();
  for (const cell of command.cells) {
    latest.set(cell.index, cell.after);
  }
  const indices = [...latest.keys()].sort((a, b) => a - b);
  const table: ValueCodes = { codes: new Map(), values: [] };
  const codes = indices.map((index) => codeFor(table, latest.get(index) as FillCellValue));
  return patchesOf(command.mapId, command.layerId, grid, { count: indices.length, indexAt: (i) => indices[i] as number, codeAt: (i) => codes[i] as number, valueOf: (code) => table.values[code] as PatchValue });
}

/**
 * The patches of a fill: its indices, in ascending order, all taking one value.
 *
 * @param command The fill.
 * @param grid The grid it writes.
 * @returns The patches.
 */
function fillPatches(command: Extract<Command, { readonly kind: "fillCells" }>, grid: PatchGrid): Intent[] {
  let ascending = true;
  for (let i = 1; i < command.indices.length && ascending; i += 1) {
    ascending = (command.indices[i] as number) > (command.indices[i - 1] as number);
  }
  const indices = ascending ? command.indices : Uint32Array.from(new Set(command.indices)).sort();
  const value: PatchValue = command.after !== null && typeof command.after === "object" ? tileWire(command.after, true) : command.after;
  return fillPatchesOf(command.mapId, command.layerId, grid, indices, value);
}

/**
 * The intents that fill an empty layer with a layer's content: the cells that are not empty as patches, and
 * the props or entities each after the one before it; a door layer's doors, each after the one before it, then
 * its cells.
 *
 * @param mapId The map's id.
 * @param layer The layer with its content.
 * @param tilesets The tileset list the layer's tiles are packed against.
 * @returns The intents, none for an empty layer.
 */
function contentIntents(mapId: string, layer: LayerDocument, tilesets: readonly TilesetRef[]): Intent[] {
  if (layer.kind === "prop") {
    return layer.props.map((prop, i) => ({ k: "addProp", map: mapId, layer: layer.id, prop: propWire(prop), after: layer.props[i - 1]?.id ?? null }));
  }
  if (layer.kind === "entity") {
    return layer.entities.map((entity, i) => ({ k: "addEntity", map: mapId, layer: layer.id, entity: entityWire(entity), after: layer.entities[i - 1]?.id ?? null }));
  }
  const doors = layer.kind === "door" ? doorListIntents(mapId, layer) : [];
  const held = gridOf(layer) as NonNullable<ReturnType<typeof gridOf>>;
  const empty = layer.kind === "tile" ? EMPTY_TILE : 0;
  let count = 0;
  for (let i = 0; i < held.cells.length; i += 1) {
    count += held.cells[i] === empty ? 0 : 1;
  }
  const indices = new Uint32Array(count);
  for (let i = 0, next = 0; i < held.cells.length; i += 1) {
    if (held.cells[i] !== empty) {
      indices[next] = i;
      next += 1;
    }
  }
  /**
   * The value a cell's number stands for: a tile by this list, a zone type by the layer's table, a door by the
   * layer's list, a byte itself.
   *
   * @param code The number the grid holds.
   * @returns The value.
   */
  const valueOf = (code: number): PatchValue => {
    if (layer.kind === "tile") {
      return packedToTileRef(code, tilesets);
    }
    if (layer.kind === "door") {
      return layer.doors[code - 1]?.id ?? null;
    }
    return layer.kind === "zone" ? (layer.types[code - 1] ?? null) : code;
  };
  return [...doors, ...patchesOf(mapId, layer.id, held.grid, { count, indexAt: (i) => indices[i] as number, codeAt: (i) => held.cells[indices[i] as number] as number, valueOf })];
}

/**
 * The intents of a layer's move: the layer above its new neighbour, on the side of the Players band that
 * leaves the band where the command leaves it, and the band named outright when no side does.
 *
 * @param command The move.
 * @param map The map before it.
 * @returns The intents, none when the command changes nothing.
 * @throws Error when the map holds no layer at the command's index.
 */
function moveIntents(command: Extract<Command, { readonly kind: "moveLayer" }>, map: MapDocument): Intent[] {
  const layer = map.layers[command.from];
  if (layer === undefined) {
    throw new Error(`map ${map.id} holds no layer at ${command.from}`);
  }
  const order = map.layers.filter((_, index) => index !== command.from);
  order.splice(command.to, 0, layer);
  const band = playersAbove(map);
  const target = command.playersAbove?.after ?? map.playersAbove ?? defaultPlayersAbove(order);
  const move = { k: "moveLayer" as const, map: map.id, layer: layer.id, above: order[command.to - 1]?.id ?? null };
  for (const side of [undefined, "ground", "band"] as const) {
    const after = bandAfterPlacing(band, command.from, command.to, side);
    if ((after ?? band) === target) {
      return after === null ? [] : [{ ...move, ...(side === undefined ? {} : { side }) }];
    }
  }
  return [...(command.from === command.to ? [] : [move]), { k: "setPlayersAbove", map: map.id, first: order[target]?.id ?? null }];
}

/**
 * The intents of a command over one map.
 *
 * @param command The command.
 * @param document The document before it.
 * @returns The intents.
 * @throws Error when the command names a map or a layer the document lacks.
 */
function mapIntents(command: Exclude<Command, { readonly kind: "compound" | "changeTilesets" | "setTerrainName" | "addMap" | "removeMap" | "renameMap" | LayoutCommand["kind"] }>, document: WorldDocument): Intent[] {
  const map = mapOf(document, command.mapId);
  if (map === undefined) {
    throw new Error(`map ${command.mapId} does not exist`);
  }
  const at = { map: map.id };
  switch (command.kind) {
    case "paintCorners":
      return strokePatches(command, "corners");
    case "paintCollision":
      return strokePatches(command, "collision");
    case "paintZone":
      return strokePatches(command, "zone");
    case "setTiles":
      return strokePatches(command, "tiles");
    case "paintDoor":
      return strokePatches(command, "door");
    case "addDoor":
    case "removeDoor":
    case "updateDoor": {
      const layer = layerOf(document, map.id, command.layerId);
      return doorCommandIntents(command, layer?.kind === "door" ? layer : undefined);
    }
    case "fillCells": {
      const layer = layerOf(document, map.id, command.layerId);
      const held = layer === undefined ? undefined : gridOf(layer);
      if (held === undefined) {
        throw new Error(`layer ${command.layerId} holds no cells to fill`);
      }
      return fillPatches(command, held.grid);
    }
    case "addLayer":
      return [{ k: "addLayer", ...at, layer: withoutContent(command.layer), above: map.layers[command.index - 1]?.id ?? null, ...(command.group === undefined ? {} : { group: { id: command.group.id, name: command.group.name } }) }, ...contentIntents(map.id, command.layer, document.tilesets)];
    case "removeLayer":
      return [{ k: "removeLayer", ...at, layer: command.layer.id, ...(command.group === undefined ? {} : { group: command.group.id }) }];
    case "addGroup":
      return [{ k: "addGroup", ...at, group: { id: command.group.id, name: command.group.name } }];
    case "removeGroup":
      return [{ k: "removeGroup", ...at, group: command.group.id }];
    case "renameGroup":
      return [{ k: "renameGroup", ...at, group: command.groupId, name: command.after }];
    case "setPlayersAbove":
      return [{ k: "setPlayersAbove", ...at, first: map.layers[command.after]?.id ?? null }];
    case "setMapProperties": {
      const changes = keyChanges(command.before, command.after);
      return changes.set === undefined && changes.remove === undefined ? [] : [{ k: "setMapProperties", ...at, ...changes }];
    }
    case "setIssueIgnores": {
      const changed = keyChanges(command.before, command.after);
      const changes: IgnoreChanges = { ...(changed.set === undefined ? {} : { add: Object.keys(changed.set) }), ...(changed.remove === undefined ? {} : { remove: changed.remove }) };
      return changes.add === undefined && changes.remove === undefined ? [] : [{ k: "setIssueIgnores", ...at, ...changes }];
    }
    case "setLayerGroup":
      return [{ k: "setLayerGroup", ...at, layer: command.layerId, group: command.after ?? null }];
    case "moveLayer":
      return moveIntents(command, map);
    case "addProp":
      return [{ k: "addProp", ...at, layer: command.layerId, prop: propWire(command.prop), after: itemBefore(document, command.mapId, command.layerId, command.index) }];
    case "removeProp":
      return [{ k: "removeProp", ...at, layer: command.layerId, prop: command.prop.id }];
    case "addEntity":
      return [{ k: "addEntity", ...at, layer: command.layerId, entity: entityWire(command.entity), after: itemBefore(document, command.mapId, command.layerId, command.index) }];
    case "removeEntity":
      return [{ k: "removeEntity", ...at, layer: command.layerId, entity: command.entity.id, type: command.entity.type }];
    case "updateEntity": {
      const changes = keyChanges(command.before.fields, command.after.fields);
      const moved = command.before.x !== command.after.x || command.before.y !== command.after.y;
      if (!moved && changes.set === undefined && changes.remove === undefined) {
        return [];
      }
      return [{ k: "updateEntity", ...at, layer: command.layerId, entity: command.entityId, ...(moved ? { at: [command.after.x, command.after.y] as const } : {}), ...changes }];
    }
    case "renameLayer":
      return [{ k: "renameLayer", ...at, layer: command.layerId, name: command.after, ...(command.autoName === undefined ? {} : { autoName: command.autoName.after ?? null }) }];
    case "setLayerTerrain":
      return [{ k: "setLayerTerrain", ...at, layer: command.layerId, terrain: command.after.terrain, variant: command.after.variant, ...(command.after.name === undefined ? {} : { name: command.after.name }), ...(command.after.faceHeight === undefined ? {} : { faceHeight: command.after.faceHeight }) }];
    case "setLayerFaceHeight":
      return [{ k: "setLayerFaceHeight", ...at, layer: command.layerId, height: command.after ?? null }];
    case "updateProp": {
      const { before, after } = command;
      const own = sameJson(before.footprint, after.footprint) ? {} : { footprint: after.footprint === undefined ? null : plainInstanceFootprint(after.footprint) };
      const fields = {
        ...(sameJson(tileWire(before.frame, false), tileWire(after.frame, false)) && (own.footprint ?? null) === null ? {} : { frame: tileWire(after.frame, false) }),
        ...(before.x === after.x && before.y === after.y ? {} : { at: [after.x, after.y] as const }),
        ...(before.flipX === after.flipX ? {} : { flipX: after.flipX }),
        ...(before.flipY === after.flipY ? {} : { flipY: after.flipY }),
        ...(before.sortPixels === after.sortPixels ? {} : { sortPixels: after.sortPixels }),
        ...(sameJson(before.behaviour, after.behaviour) ? {} : { behaviour: after.behaviour === undefined ? null : behaviourWire(after.behaviour) }),
        ...((before.keepPlacement === true) === (after.keepPlacement === true) ? {} : { keepPlacement: after.keepPlacement === true }),
        ...own,
      };
      return Object.keys(fields).length === 0 ? [] : [{ k: "updateProp", ...at, layer: command.layerId, prop: command.propId, ...fields }];
    }
  }
}

/**
 * The id of the item an added item goes after: the one before its index in its layer.
 *
 * @param document The document before the add.
 * @param mapId The map.
 * @param layerId The layer.
 * @param index The index the item is added at.
 * @returns The id, or null for the first place or a layer that holds no items.
 */
function itemBefore(document: WorldDocument, mapId: string, layerId: string, index: number): string | null {
  const layer = layerOf(document, mapId, layerId);
  const items = layer?.kind === "prop" ? layer.props : layer?.kind === "entity" ? layer.entities : [];
  return items[index - 1]?.id ?? null;
}

/**
 * Appends a command's intents to a list.
 *
 * @param command The command.
 * @param document The document before it.
 * @param out The list.
 * @throws Error when the command does not apply to the document, or sets a whole tileset entry's frames, which no
 *   intent carries; `commandToOps` answers either with null.
 */
function collect(command: Command, document: WorldDocument, out: Intent[]): void {
  switch (command.kind) {
    case "compound": {
      const parts = command.commands;
      let current = document;
      for (let i = 0; i < parts.length; ) {
        const end = updatePropRunEnd(parts, i);
        for (let k = i; k < end; k += 1) {
          collect(parts[k] as Command, current, out);
        }
        if (end < parts.length) {
          current = end - i > 1 ? applyPropRun(current, parts.slice(i, end) as UpdatePropCommand[]) : applyCommand(current, parts[i] as Command);
        } else if (end - i > 1) {
          applyPropRun(current, parts.slice(i, end - 1) as UpdatePropCommand[]);
        }
        i = end;
      }
      return;
    }
    case "changeTilesets":
      out.push(...tilesetIntents(command));
      return;
    case "setTerrainName":
      out.push({ k: "setTerrainName", entry: command.entry, name: command.after ?? null });
      return;
    case "addMap":
      out.push({ k: "addMap", map: withoutLayerContent(command.map), after: document.maps[command.index - 1]?.id ?? null });
      for (const layer of command.map.layers) {
        out.push(...contentIntents(command.map.id, layer, document.tilesets));
      }
      return;
    case "removeMap":
      out.push({ k: "removeMap", map: command.map.id });
      return;
    case "renameMap":
      out.push({ k: "renameMap", map: command.mapId, name: command.after });
      return;
    case "placeMap":
    case "setGrid":
    case "setMapGroups":
    case "setMapGroup":
    case "setMapDraft":
    case "setMapHost":
    case "setMapShare":
    case "setMapCapacity":
    case "setMapAtmosphere":
    case "setSeam":
    case "removeSeam":
    case "setCrossing":
    case "removeCrossing":
      out.push(...layoutIntents(command));
      return;
    default:
      out.push(...mapIntents(command, document));
  }
}

/**
 * Turns a command into the operations a tab sends for it.
 *
 * @param command The command, a compound included.
 * @param document The document before the command.
 * @param ids The tab's tag and the counter the first operation takes.
 * @returns The operations in the order to send them, all of one edit group named after the first, each a
 *   frame `parseOp` reads; none for a command that writes nothing; or null when a part cannot travel: an
 *   intent longer than a frame by itself (an entity whose fields do not fit), or a value the codec refuses
 *   (an id outside the document id rule, a name past its length), or a command that does not apply to the
 *   document (a map or a layer it names is missing, a part of a compound the document refuses).
 */
export function commandToOps(command: Command, document: WorldDocument, ids: OpIds): readonly LiveOp[] | null {
  const intents: Intent[] = [];
  try {
    collect(command, document, intents);
  } catch {
    return null;
  }
  const group = `${ids.tag}-${ids.next}`;
  const ops: LiveOp[] = [];
  let list: Intent[] = [];
  let length = 0;
  let cells = 0;
  /**
   * Ends the operation being filled, when it holds an intent, and starts the next.
   */
  const close = (): void => {
    if (list.length > 0) {
      ops.push({ id: `${ids.tag}-${ids.next + ops.length}`, group, list });
      list = [];
      length = 0;
      cells = 0;
    }
  };
  for (const intent of intents) {
    const size = JSON.stringify(intent).length + 1;
    const covered = cellsOf([intent]);
    if (size + OP_FRAME_OVERHEAD > OP_FRAME_MAX) {
      return null;
    }
    if (list.length === OP_INTENTS_MAX || length + size + OP_FRAME_OVERHEAD > OP_FRAME_MAX || cells + covered > OP_CELLS_MAX) {
      close();
    }
    list.push(intent);
    length += size;
    cells += covered;
  }
  close();
  return ops.every((op) => parseOp(encodeOp(op)) !== null) ? ops : null;
}
